/**
 * @file legal-errors.ts
 * @description Текст ошибки API документов для человека (phase19.16,
 * SB139–SB144; uiux-legal-docs §7).
 *
 * Код ошибки → текст из `LEGAL_ERROR_MESSAGES` (shared, одни тексты для
 * кабинета и тестов API). Особый случай — 409 `legal_draft_changed`: кабинет
 * говорит короче и по делу («изменён в другом окне — обновите страницу»).
 * Прежний код API `legal_document_slug_conflict` читается как «адрес занят».
 * Остальные `ApiError` — сообщение сервера; всё прочее — запасной текст.
 */

import { LEGAL_ERROR_MESSAGES, type LegalErrorCode } from "@chaiberi/shared";

import { ApiError } from "../api-client";
import { UPLOAD_ABORTED_CODE } from "../upload-with-progress";

/** Текст 409 `legal_draft_changed` в кабинете. */
export const LEGAL_DRAFT_CHANGED_TEXT = "Документ изменён в другом окне — обновите страницу.";

/** Нет связи с API. */
const NETWORK_TEXT = "Нет связи с сервером — проверьте интернет и повторите.";

/** Прежние коды API (до phase19.16) → новый код. */
const LEGACY_CODES: Readonly<Record<string, LegalErrorCode>> = {
  legal_document_slug_conflict: "legal_slug_taken",
};

/** Код ошибки документов, если это он. */
export function legalErrorCode(err: unknown): LegalErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  const code = LEGACY_CODES[err.code] ?? err.code;
  return Object.hasOwn(LEGAL_ERROR_MESSAGES, code) ? (code as LegalErrorCode) : null;
}

/** Отмена загрузки пользователем — не ошибка, toast не нужен. */
export function isUploadAborted(err: unknown): boolean {
  return err instanceof ApiError && err.code === UPLOAD_ABORTED_CODE;
}

/** Человеческий текст ошибки; `fallback` — когда сказать нечего. */
export function legalErrorText(err: unknown, fallback: string): string {
  const code = legalErrorCode(err);
  if (code === "legal_draft_changed") return LEGAL_DRAFT_CHANGED_TEXT;
  if (code) return LEGAL_ERROR_MESSAGES[code];
  if (err instanceof ApiError) {
    if (err.code === "network_error") return NETWORK_TEXT;
    // «HTTP 500» человеку ничего не говорит.
    return err.message && !/^HTTP \d+$/u.test(err.message) ? err.message : fallback;
  }
  return fallback;
}
